import { observable, action, computed, makeObservable } from 'mobx';
import { LoadingTracker, LoadStatus } from '~/lib/store/LoadingTracker';

type LoaderFunction<T> = () => Promise<T>;

export class AsyncStore<T, Tinitial = T> {
    constructor(
        private loader: LoaderFunction<T>,
        initialValue: Tinitial
    ) {
        this.item = initialValue;
        makeObservable(this);
    }

    @observable
    loadingTracker: LoadingTracker = new LoadingTracker();

    @computed
    get status() {
        return this.loadingTracker.status;
    }

    @computed
    get lastError() {
        return this.loadingTracker.lastError;
    }

    @observable
    item: T | Tinitial;

    @action
    setItem(item: T): T {
        this.item = item;
        return this.item;
    }

    /**
     * Loads and sets the item. If a newer load supersedes this one, resolves with the raw
     * response, not the store's observable `item`, and leaves the store unchanged.
     */
    async load(): Promise<T> {
        return this.loadingTracker.track(async (isCurrent) => {
            const res = await this.loader();
            // Only the latest request sets the item; setting it here keeps it ahead of "Complete".
            return isCurrent() ? this.setItem(res) : res;
        });
    }

    async ensureLoaded(): Promise<T> {
        if (this.status === LoadStatus.Complete) return this.item as T; // we know that item is set so we cast to T

        if (this.status === LoadStatus.Loading) {
            await this.loadingTracker.asPromise();
            return this.item as T; // we know that item is set so we cast to T
        }

        //pending or errored, we just reload
        return this.load();
    }
}
